import {userSellerService, projectSellerService, accountSellerService} from 'services/seller';
import {
  SELLER_META_HAS_SHOW_WELCOME,
  SELLER_META_HAS_SHOW_MODAL_CREATE_FIRST_STRATEGY,
  SELLER_META_TRIAL_DATE_LEFT_SUBSCRIPTION,
  SELLER_META_HAS_FINISH_ONBOARDING_CAMPAIGN_LIST_FIRST,
  SELLER_META_FILTER_STATUS_LIST,
  SELLER_META_INVALID_SHOPS_LIST,
  SELLER_META_IS_PERFORMANCE_WIDGET_ENABLED,
  SELLER_META_TECH_WORKS_VIEWED_LIST,
  SELLER_META_VAT_INCREASE_NOTIFY_VIEWED_2026
} from 'utils/seller/sellerMetaUtils';
import {getDaysLeft} from 'utils/Date/dateUtils';
import moment from 'moment-wrapper';
import BigNumber from 'bignumber.js';
import {getSellerFactStatusTypesListForSort} from 'utils/seller/sellerStatusUtils';

const DEFAULT_VAT_DIVIDER = 1;
const CHANGE_PLACEMENT_CONTROL_FEATURE_RELEASE_DATE = '2025-12-19 09:00';

export const SET_USER = 'SET_USER';
export const SET_ACCOUNT = 'SET_ACCOUNT';
export const SET_PROJECT = 'SET_PROJECT';
export const AUTH_USER = 'AUTH_USER';
export const SET_META_USER = 'SET_META_USER';
export const SET_META_PROJECT = 'SET_META_PROJECT';
export const SET_TO_STORE_USER_META = 'SET_TO_STORE_USER_META';
export const GET_META_USER = 'GET_META_USER';
export const SET_REDIRECT = 'SET_REDIRECT';
export const AUTH_CURRENT_USER = 'AUTH_CURRENT_USER';
export const FETCH_USER_BACKWARD = 'FETCH_USER_BACKWARD';
export const FETCH_PROJECT_AND_ACCOUNT = 'FETCH_PROJECT_AND_ACCOUNT';
export const FETCH_PROJECT = 'FETCH_PROJECT';
export const FETCH_ACCOUNT = 'FETCH_ACCOUNT';
export const UPDATE_PLACEMENT_CONTROL_STATUS = 'UPDATE_PLACEMENT_CONTROL_STATUS';

const CALENDAR_PARAMS = {
  dateInputPattern: ['d', 'm', 'Y'],
  dateInputPatternShort: ['d', 'm', 'y'],
  dateInputDelimiter: '.'
};

const userStore = {
  namespaced: true,
  state: {
    user: null,
    account: null,
    project: null,
    userMeta: null,
    cabinetRedirect: {
      trial: null,
      main: null
    }
  },
  getters: {

    // account vat
    getVatDividerForDate: (state, getters) => (date) => {
      if (!getters.currentUser.with_vat) {
        return false;
      }
      const currentAccount = getters.currentAccount;
      if (!currentAccount) {
        console.warn('[VUEX] No account for VAT, using default value {0}'.format(DEFAULT_VAT_DIVIDER));
        return DEFAULT_VAT_DIVIDER;
      }
      const vatRates = currentAccount.vats || [];
      date = moment(date || undefined);
      for (let i = vatRates.length - 1; i >= 0; i = i - 1) {
        const vat = vatRates[i];
        if (moment(vat.date) <= date) {
          return new BigNumber(vat.value);
        }
      }
      console.warn('[VAT] Rate not found for {0}. Using {1} as VAT'.format(
        date.format('YYYY-MM-DD'),
        DEFAULT_VAT_DIVIDER
      ));
      return new BigNumber(DEFAULT_VAT_DIVIDER);
    },
    userMeta (state) {
      return state.userMeta;
    },
    techWorksNotViewed (state, getters) {
      if (!getters.currentUser?.tech_works?.length) {
        return [];
      }

      if (getters.techWorksViewedList?.length) {
        return getters.currentUser?.tech_works.filter(item => !getters.techWorksViewedList.includes(item.id));
      }

      return getters.currentUser?.tech_works || [];
    },
    techWorksViewedList (state) {
      return state.userMeta[SELLER_META_TECH_WORKS_VIEWED_LIST] || [];
    },
    isUserAdmin (state) {
      return state.user.is_admin;
    },
    vatDivider (state, getters) {
      return getters.getVatDividerForDate();
    },
    currentUser (state) {
      return state.user;
    },
    currentAccount (state) {
      return state.account;
    },
    currentProject (state) {
      return state.project;
    },
    subscriptionData (state) {
      return state.userMeta.subscription;
    },
    isTrialSubscription (state, getters) {
      return getters.subscriptionData.state === 'TRIAL';
    },
    isWaitTrialSubscription (state, getters) {
      return getters.subscriptionData.state === 'WAIT_TRIAL';
    },
    isMainSubscription (state, getters) {
      return getters.subscriptionData.state === 'ACTIVE';
    },
    isCanceledSubscription (state, getters) {
      return getters.subscriptionData.state === 'CANCELED';
    },
    isWaitingForMoneySubscription (state, getters) {
      return getters.subscriptionData.state === 'WAITING_FOR_MONEY';
    },
    hasFirstStatCollectionDone (state) {
      const value = state.userMeta?.stats_has_been_collected;

      if (value === undefined) {
        return null;
      }

      return value;
    },
    subscriptionIsActive (state, getters) {
      const isDueDateLeft = Number(getDaysLeft(getters.subscriptionData.due_date)) >= -1;
      return !getters.isCanceledSubscription && (getters.isWaitTrialSubscription || isDueDateLeft);
    },
    cabinetRedirect (state) {
      return state.cabinetRedirect;
    },
    userLocale (state) {
      return state.user.locale;
    },
    userLocaleFull (state) {
      let locale = 'ru-RU';
      if (state.user.locale === 'en') {
        locale = 'en-EU';
      }
      return locale;
    },
    hasNotShowWelcome (state) {
      return !state.userMeta[SELLER_META_HAS_SHOW_WELCOME];
    },
    hasConnectionsFromTR (state, getters) {
      return Boolean(getters.getConnectionIdList.length);
    },
    getFilterStatusList (state, getters) {
      if (getters.isCanceledSubscription) {
        return getSellerFactStatusTypesListForSort().
          map(item => item.id);
      }

      const userFilterList = state.userMeta[SELLER_META_FILTER_STATUS_LIST];

      return userFilterList ||
        ['RUNNING', 'PAUSED', 'PAUSED_BY_SCHEDULE', 'BUDGET_IS_EXHAUSTED', 'PAUSED_BY_BID_MANAGER'];
    },
    getTrialDateLeftSubscription (state) {
      return state.userMeta[SELLER_META_TRIAL_DATE_LEFT_SUBSCRIPTION] || null;
    },
    isNeedShowOnboardingCampaignListFirst (state) {
      return !state.userMeta[SELLER_META_HAS_FINISH_ONBOARDING_CAMPAIGN_LIST_FIRST];
    },
    getInvalidShopsList (state) {
      return state.userMeta[SELLER_META_INVALID_SHOPS_LIST] || [];
    },
    isPerformanceWidgetEnabled (state) {
      return state.userMeta[SELLER_META_IS_PERFORMANCE_WIDGET_ENABLED] ?? true;
    },
    isNeedShowCreateFirstStrategyModal (state) {
      return !state.userMeta[SELLER_META_HAS_SHOW_MODAL_CREATE_FIRST_STRATEGY];
    },
    getAccountId (state, getters, rootState, rootGetters) {
      return rootGetters['sellerCommon/sellerAccountId'];
    },
    getProjectId (state) {
      return state.userMeta.project_id;
    },
    calendarLocaleParams () {
      return CALENDAR_PARAMS;
    },
    getConnectionIdList (state) {
      return state.userMeta.connection_ids || [];
    },
    isVATIncreaseNotifyViewed2026 (state) {
      return state.userMeta[SELLER_META_VAT_INCREASE_NOTIFY_VIEWED_2026] ?? false;
    },
    hasAccountLegacyPlacementsControl (state, getters) {
      const changePlacementsControlReleaseDate = moment(CHANGE_PLACEMENT_CONTROL_FEATURE_RELEASE_DATE);
      const cabinetCreationDate = moment(getters.currentAccount.created_at);

      return cabinetCreationDate.isBefore(changePlacementsControlReleaseDate, 'hour');
    },
    isPlacementsControlEnabled (state) {
      return state.project.manage_placements;
    }

  },
  mutations: {
    [SET_USER] (state, user) {
      state.user = user;
    },
    [SET_ACCOUNT] (state, account) {
      state.account = account;
    },
    [SET_PROJECT] (state, project) {
      state.project = project;
    },
    [SET_USER] (state, user) {
      state.user = user;
    },
    [SET_REDIRECT] (state, cabinetRedirect) {
      state.cabinetRedirect = cabinetRedirect;
    },
    [SET_TO_STORE_USER_META] (state, userMeta) {
      state.userMeta = userMeta;
    }
  },
  actions: {
    [SET_USER] (context, user) {
      context.commit(SET_USER, user);
    },
    [SET_TO_STORE_USER_META] (context, userMeta) {
      context.commit(SET_TO_STORE_USER_META, userMeta);
    },
    [SET_ACCOUNT] (context, account) {
      context.commit(SET_ACCOUNT, account);
    },
    [SET_PROJECT] (context, project) {
      context.commit(SET_PROJECT, project);
    },
    [SET_REDIRECT] (context, cabinetRedirect) {
      context.commit(SET_REDIRECT, {
        trial: cabinetRedirect.subscriptions_trial_url,
        main: cabinetRedirect.subscriptions_url
      });
    },
    async [AUTH_CURRENT_USER] (context) {
      const user = await userSellerService.getCurrentUser();
      return context.dispatch(SET_USER, user);
    },
    async [FETCH_PROJECT] (context) {
      const project = await projectSellerService.getCurrentProject(context.getters.getProjectId);
      context.dispatch(SET_PROJECT, project);
    },
    async [FETCH_ACCOUNT] (context) {
      const account = await accountSellerService.getCurrentAccount(context.rootGetters['sellerCommon/sellerAccountId']);
      context.dispatch(SET_ACCOUNT, account);
    },
    async [FETCH_PROJECT_AND_ACCOUNT] (context) {
      await Promise.all([
        context.dispatch(FETCH_PROJECT),
        context.dispatch(FETCH_ACCOUNT)
      ]);
    },
    async [FETCH_USER_BACKWARD] (context) {
      const cabinetRedirect = await userSellerService.getBackward();
      return context.dispatch(SET_REDIRECT, cabinetRedirect);
    },
    async [AUTH_USER] (context, temporaryAuthToken) {
      return await userSellerService.authUser({code: temporaryAuthToken});
    },
    async [SET_META_USER] (context, payload) {
      await userSellerService.setMeta(payload);
      await context.dispatch(GET_META_USER);

      return context.getters.userMeta;
    },
    async [SET_META_PROJECT] (context, payload) {
      await userSellerService.setMetaProject(payload);
      await context.dispatch(GET_META_USER);

      return context.getters.userMeta;
    },
    async [GET_META_USER] (context) {
      const userMeta = await userSellerService.getMeta();

      context.dispatch(SET_TO_STORE_USER_META, userMeta.meta);

      const campaignsLastRefreshAt = context.getters.userMeta?.campaigns_last_refresh_at;
      if (campaignsLastRefreshAt) {
        context.commit('sellerCampaigns/SET_PLACEMENT_LIST_REFRESH_TIME', campaignsLastRefreshAt, {root: true});
      }

      return userMeta;
    },
    async [UPDATE_PLACEMENT_CONTROL_STATUS] (context, payload) {
      await context.dispatch(FETCH_PROJECT);
      const currentProject = context.getters.currentProject;
      const projectId = context.getters.getProjectId;

      const updatedProject = await projectSellerService.updateCurrentProject(
        projectId,
        {...currentProject, ...{manage_placements: payload}}
      );

      context.dispatch(SET_PROJECT, updatedProject);
    }
  }
};

export default userStore;
